
import { Component, Prop, Vue } from 'vue-property-decorator';

import LabeledDonutChart from '../components/LabeledDonutChart.vue';
import RiskAssessmentRatingKey from '../components/RiskAssessmentRatingKey.vue';
import RiskBreakdownChart from '../components/RiskBreakdownChart.vue';
import {
  AssessmentInstance,
  AssessmentInstanceThreat,
  Question,
  QuestionOption,
  RiskAssessmentRating,
  Section,
  ThreatLevel,
  ThreatWithQuestions,
} from '../models';

@Component({
  components: {
    LabeledDonutChart,
    RiskAssessmentRatingKey,
    RiskBreakdownChart,
  },
})
export default class SummaryContainer extends Vue {
  @Prop(Object) private readonly assessment!: AssessmentInstance;

  get sortedSections(): Section[] {
    return this.assessment.sections.sort((s1, s2) => s1.sortOrder - s2.sortOrder);
  }

  get allQuestions(): Question[] {
    return this.assessment.sections.flatMap((s) => s.questions);
  }

  get allOptions(): QuestionOption[] {
    return this.allQuestions.flatMap((q) => q.options);
  }

  get answerIdToOptionMap(): Map<number, QuestionOption> {
    const map = new Map<number, QuestionOption>();
    for (const answer of this.assessment.answers) {
      const option = this.allOptions.find((o) => o.id === answer.questionOptionId)!;
      map.set(answer.id!, option);
    }
    return map;
  }

  get questionIdToSectionIdMap(): Map<number, number> {
    const map = new Map<number, number>();
    for (const question of this.allQuestions) {
      map.set(question.id, question.sectionId);
    }
    return map;
  }

  get selectedOptionIdToQuestionMap(): Map<number, Question> {
    const map = new Map<number, Question>();
    for (const option of this.selectedOptions) {
      const question = this.allQuestions.find((q) => q.id === option.questionId)!;
      map.set(option.id, question);
    }
    return map;
  }

  get selectedOptionIdToOptionMap(): Map<number, QuestionOption> {
    const map = new Map<number, QuestionOption>();
    for (const option of this.selectedOptions) {
      map.set(option.id, option);
    }
    return map;
  }

  get sectionIdToSelectedOptionIdsMap(): Map<number, number[]> {
    const map = new Map<number, number[]>();
    for (const section of this.assessment.sections) {
      map.set(section.id, []);
    }
    for (const option of this.selectedOptions) {
      const sectionId = this.questionIdToSectionIdMap.get(option.questionId)!;
      map.get(sectionId)!.push(option.id);
    }
    return map;
  }

  private getRiskScoreBySectionId(sectionId: number): number {
    const optionIds = this.sectionIdToSelectedOptionIdsMap.get(sectionId)!;
    const badOptionIds = optionIds.filter((i) => this.badOptionIds.includes(i));
    return Math.floor((badOptionIds.length / optionIds.length) * 100);
  }

  get sectionIdToRiskScoreMap(): Map<number, number> {
    const map = new Map<number, number>();
    for (const section of this.assessment.sections) {
      map.set(section.id, this.getRiskScoreBySectionId(section.id));
    }
    return map;
  }

  get selectedOptions(): QuestionOption[] {
    return Array.from(this.answerIdToOptionMap.values());
  }

  get selectedOptionCount(): number {
    return this.selectedOptions.length;
  }

  get badOptions(): QuestionOption[] {
    return this.selectedOptions.filter((o) => o.isBad);
  }

  get badOptionIds(): number[] {
    return this.badOptions.map((o) => o.id);
  }

  get badOptionCount(): number {
    return this.badOptions.length;
  }

  get badOptionPercentage(): number {
    return Math.floor((this.badOptionCount / this.selectedOptionCount) * 100);
  }

  get allThreats(): ThreatWithQuestions[] {
    return this.assessment.sections.flatMap((s) => s.threats!);
  }

  get applicableThreats(): ThreatWithQuestions[] {
    return this.allThreats.filter((t) => t.assessmentInstanceThreat.isApplicable);
  }

  get sectionIdToApplicableThreatsMap(): Map<number, ThreatWithQuestions[]> {
    const map = new Map<number, ThreatWithQuestions[]>();
    for (const section of this.assessment.sections) {
      map.set(section.id, []);
      for (const threat of section.threats!) {
        if (threat.assessmentInstanceThreat.isApplicable) {
          map.get(section.id)!.push(threat);
        }
      }
    }
    return map;
  }

  get threatIdToRiskAssessmentRatingMap(): Map<number, RiskAssessmentRating> {
    const map = new Map<number, RiskAssessmentRating>();
    for (const threat of this.applicableThreats) {
      const rating = this.calculateRiskAssessmentRating(threat.assessmentInstanceThreat);
      const threatId = threat.assessmentInstanceThreat.threat.id;
      map.set(threatId, rating);
    }
    return map;
  }

  get riskAssessmentRatingMap(): Map<RiskAssessmentRating, ThreatWithQuestions[]> {
    const map = new Map<RiskAssessmentRating, ThreatWithQuestions[]>();
    map.set(RiskAssessmentRating.Low, []);
    map.set(RiskAssessmentRating.Medium, []);
    map.set(RiskAssessmentRating.High, []);
    map.set(RiskAssessmentRating.Critical, []);
    for (const threat of this.applicableThreats) {
      const rating = this.calculateRiskAssessmentRating(threat.assessmentInstanceThreat);
      map.get(rating)!.push(threat);
    }
    return map;
  }

  private calculateRiskAssessmentRating(threat: AssessmentInstanceThreat): RiskAssessmentRating {
    if (threat.likelihood === ThreatLevel.Low) {
      if (threat.impact === ThreatLevel.Low) {
        return RiskAssessmentRating.Low;
      } else if (threat.impact === ThreatLevel.Medium) {
        return RiskAssessmentRating.Medium;
      } else {
        return RiskAssessmentRating.High;
      }
    } else if (threat.likelihood === ThreatLevel.Medium) {
      if (threat.impact === ThreatLevel.Low) {
        return RiskAssessmentRating.Low;
      } else if (threat.impact === ThreatLevel.Medium) {
        return RiskAssessmentRating.Medium;
      } else {
        return RiskAssessmentRating.Critical;
      }
    } else {
      if (threat.impact === ThreatLevel.Low) {
        return RiskAssessmentRating.Medium;
      } else if (threat.impact === ThreatLevel.Medium) {
        return RiskAssessmentRating.High;
      } else {
        return RiskAssessmentRating.Critical;
      }
    }
  }

  get threatIdToVulnerabilityMap(): Map<number, string> {
    const map = new Map<number, string>();
    for (const threat of this.allThreats) {
      const threatId = threat.assessmentInstanceThreat.threat.id;
      const vulnerability = threat.assessmentInstanceThreat.threat.vulnerability;
      map.set(threatId, vulnerability);
    }
    return map;
  }

  get sectionIdToVulnerabilitiesMap(): Map<number, Set<string>> {
    const map = new Map<number, Set<string>>();
    for (const section of this.assessment.sections) {
      map.set(section.id, new Set());
      for (const threat of section.threats!) {
        if (threat.assessmentInstanceThreat.isApplicable) {
          map.get(section.id)!.add(threat.assessmentInstanceThreat.threat.vulnerability);
        }
      }
    }
    return map;
  }

  get vulnerabilityToThreatsMap(): Map<string, ThreatWithQuestions[]> {
    const map = new Map<string, ThreatWithQuestions[]>();
    for (const threat of this.applicableThreats) {
      const vulnerability = threat.assessmentInstanceThreat.threat.vulnerability;
      if (!map.has(vulnerability)) {
        map.set(vulnerability, []);
      }
      map.get(vulnerability)!.push(threat);
    }
    return map;
  }

  get applicableVulnerabilityCount(): number {
    return this.vulnerabilityToThreatsMap.size;
  }

  get applicableVulnerabilityPercentage(): number {
    return Math.floor((this.applicableVulnerabilityCount / this.totalVulnerabilityCount) * 100);
  }

  get totalVulnerabilityCount(): number {
    return new Set(this.allThreats.map((t) => t.assessmentInstanceThreat.threat.vulnerability)).size;
  }
}
